import { useEffect, useState } from 'react';
import type { ReviewReport } from '../api/types';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { usePlatform } from '../app/platform';
import type { Route } from '../app/router';
import { useAsync } from '../app/useAsync';
import { useDraftReview } from '../app/useDraftReview';
import { useDraftTrial } from '../app/useDraftTrial';
import { useStudioEvents } from '../app/useStudioEvents';
import { PublishDialog } from './PublishDialog';
import { ReviewPanel } from './ReviewPanel';
import { RunStrip } from './RunStrip';
import { TryButton } from './TryButton';

export interface ReviewTabProps {
  workspaceId: string;
  appId: string;
  /** 公開ダイアログの見出しに出す microapp の名前（省略すると appId。B7・C7）。 */
  appName?: string;
  /** 指摘の「ファイルへ」。AppPage はコードタブのそのファイルへ移る。 */
  onOpenFile(path: string): void;
  /** run strip の Link が使う（S5）。渡さないと素の遷移になり SPA が読み直される。 */
  navigate(route: Route, options?: { replace?: boolean }): void;
}

/**
 * microapp 詳細の「レビュー」タブ（D28）。Draft の head の検証レポートと「試す」を止めている理由。
 * fixture には Draft の経路が無いので API を呼ばない（B16・C45）。
 */
export function ReviewTab(props: ReviewTabProps) {
  const { mode } = usePlatform();
  if (mode === 'fixture') {
    return <EmptyState title="fixture モードではレビューを使えません" description="実 API のモードで開いてください。" />;
  }
  return <ReviewTabBody {...props} />;
}

function ReviewTabBody({ workspaceId, appId, appName, onOpenFile, navigate }: ReviewTabProps) {
  const { api, mode } = usePlatform();
  const state = useDraftReview({ api, workspaceId, appId, enabled: true });
  const { review } = state;

  const trialState = useDraftTrial({
    api,
    workspaceId,
    appId,
    // fixture モードでは何も呼ばない（A12・B16・C45）。
    enabled: mode === 'http',
    initial: review?.trial ?? null,
    onChanged: state.reload,
  });
  const deployed =
    trialState.trial !== null && trialState.trial.status !== 'deploying' && trialState.trial.status !== 'deploy_failed';
  const definition = useAsync(
    () => (deployed ? api.getDefinition(workspaceId, appId) : Promise.resolve(null)),
    [api, workspaceId, appId, deployed],
  );
  const buildReportId = review?.build.reportId ?? null;
  const buildReport = useAsync<ReviewReport | null>(
    () => (buildReportId !== null ? api.getDraftReport(workspaceId, appId, buildReportId) : Promise.resolve(null)),
    [api, workspaceId, appId, buildReportId],
  );
  const [publishing, setPublishing] = useState(false);

  // 購読の cursor は読んだレビューのものに固定する（イベントごとに reload しても張り直さない。A8）。
  // 期限切れ（expired）のときだけ、取り直したレビューの新しい cursor に差し替える。
  const [cursor, setCursor] = useState<string | null>(null);
  const [expiredCursor, setExpiredCursor] = useState<string | null>(null);
  if (review !== null && (cursor === null || cursor === expiredCursor) && review.eventCursor !== expiredCursor) {
    setCursor(review.eventCursor);
  }

  const streamState = useStudioEvents({
    api,
    workspaceId,
    cursor,
    enabled: cursor !== null,
    onEvent: (event) => {
      // W/studio/events は Workspace 全体を流す（A8）。この app の検証と版の変更だけで取り直す。
      if (event.appId !== appId) return;
      if (event.type === 'studio.report' || event.type === 'studio.revision') state.reload();
      // 対象の試運転は SSE の trialId で読む（AI が作った新しい試運転も帯に出す。A2・G30）。
      if (event.type === 'studio.trial') trialState.reload(event.trialId);
      if (event.type === 'studio.publication') state.reload();
    },
  });
  useEffect(() => {
    if (streamState !== 'expired') return;
    setExpiredCursor(cursor);
    state.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  if (state.notFound) {
    return <EmptyState title="この microapp には Draft がありません" description="コードタブで Draft を作ると検証できます。" />;
  }
  if (review === null) {
    if (state.problem) {
      return <ProblemNotice problem={state.problem} action={<Button onClick={state.reload}>再取得</Button>} />;
    }
    return <Spinner label="レビューを取得しています" />;
  }
  return (
    <div className="stack">
      {/* fixture モードでは S5 の導線を出さない（A12・B16・C45）。 */}
      {mode === 'http' && (
        <div className="studio-app-actions">
          <TryButton
            review={review}
            onTry={(confirmReplace) => void trialState.deploy(confirmReplace)}
            busy={trialState.deploying}
          />
          {/* 公開は利用者の操作だけ（設計 03 章 L5）。microapp 詳細からも公開できる。 */}
          <Button onClick={() => setPublishing(true)} disabled={review === null}>
            公開
          </Button>
        </div>
      )}
      {trialState.trial !== null ? (
        <RunStrip
          workspaceId={workspaceId}
          appId={appId}
          trial={trialState.trial}
          headDigest={review.filesDigest}
          processes={definition.data?.processes ?? []}
          processesLoading={definition.loading}
          starting={trialState.starting}
          problem={trialState.problem ?? definition.problem}
          onStart={(processKey, input) => void trialState.start(processKey, input)}
          onDismiss={trialState.dismiss}
          onReload={() => {
            // 帯の「再取得」は試運転と、配備した定義（開始フォームの元）の両方を読み直す（A4・A11）。
            trialState.reload();
            definition.reload();
          }}
          // チャットが無いので「AI に原因を聞く」は出さない（D30）。
          navigate={navigate}
        />
      ) : (
        // 帯の元になる試運転がまだ無いときの配備の失敗（422 export_invalid・409 draft_has_errors など）。
        <ProblemNotice
          problem={trialState.problem}
          tone={trialState.problem !== null && trialState.problem.status === 503 ? 'notice' : 'error'}
        />
      )}
      <ProblemNotice problem={state.problem} tone="notice" action={<Button onClick={state.reload}>再取得</Button>} />
      <ReviewPanel
        review={review}
        validating={state.validating}
        validateProblem={state.validateProblem}
        onValidate={() => void state.validate()}
        targetOf={(finding) => {
          // プロセス・判定タブは現行パッケージを描くので、Draft の要素 id へは飛ばない（D28）。
          const path = finding.path;
          return path === null ? null : { label: 'ファイルへ', onOpen: () => onOpenFile(path) };
        }}
      />
      {/* review は DraftReview | null なので、null のときはダイアログを開かない（PublishDialogProps.review は省略不可）。 */}
      {publishing && review !== null && (
        <PublishDialog
          workspaceId={workspaceId}
          appId={appId}
          appName={appName ?? appId}
          review={review}
          // build のレポートが無ければ配備した定義の登録簿を使う（T16-5）。
          declarations={buildReport.data?.declarations ?? definition.data?.declarations ?? null}
          onPublished={() => {
            setPublishing(false);
            state.reload();
          }}
          onClose={() => setPublishing(false)}
        />
      )}
    </div>
  );
}
